<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { COBROWSE_OVERLAY_CLASS } from '~/app/cobrowse/classes/CobrowseRecorder';
    import type { CursorKind } from '~/app/cobrowse/classes/CommandExecutor';
    import { useCobrowseStore } from '~/stores/cobrowseStore';

    // Hotspot offsets put the tip of each icon, not its corner, on the agent's pointer.
    const ICONS: Record<Exclude<CursorKind, 'default'>, { icon: string; offset: string }> = {
        pointer: { icon: 'fa-solid fa-hand-pointer', offset: '-7px, -1px' },
        text: { icon: 'fa-solid fa-i-cursor', offset: '-50%, -50%' },
        'not-allowed': { icon: 'fa-solid fa-ban', offset: '-50%, -50%' },
        grab: { icon: 'fa-solid fa-hand', offset: '-50%, -50%' },
        move: { icon: 'fa-solid fa-arrows-up-down-left-right', offset: '-50%, -50%' },
    };

    const { cursor, lastClick, agentName } = storeToRefs(useCobrowseStore());
    const icon = computed(() => (cursor.value && cursor.value.kind !== 'default' ? ICONS[cursor.value.kind] : null));
</script>

<template>
    <div
        v-if="cursor"
        :class="COBROWSE_OVERLAY_CLASS"
        class="pointer-events-none fixed inset-0 z-[2147483000] overflow-hidden"
        aria-hidden="true"
    >
        <span
            v-if="lastClick"
            :key="lastClick.at"
            class="absolute -ml-4 -mt-4 size-8 rounded-full border-2 border-primary animate-cobrowse-click motion-reduce:hidden"
            :style="{ left: `${lastClick.point.x}px`, top: `${lastClick.point.y}px` }"
        />
        <div
            class="absolute left-0 top-0 transition-transform duration-100 ease-out motion-reduce:transition-none"
            :style="{ transform: `translate3d(${cursor.x}px, ${cursor.y}px, 0)` }"
        >
            <i
                v-if="icon"
                class="absolute left-0 top-0 text-xl text-primary drop-shadow"
                :class="icon.icon"
                :style="{ transform: `translate(${icon.offset})` }"
            />
            <svg
                v-else
                class="absolute -left-1 -top-0.5 size-6 drop-shadow"
                viewBox="0 0 24 24"
                fill="none"
            >
                <path
                    d="M4 2.5 19.5 12l-7 1.6L9 20.5 4 2.5Z"
                    class="fill-primary stroke-white"
                    stroke-width="1.5"
                    stroke-linejoin="round"
                />
            </svg>
            <span
                v-if="agentName"
                class="absolute left-5 top-5 inline-block whitespace-nowrap rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-white shadow-popup"
            >
                {{ agentName }}
            </span>
        </div>
    </div>
</template>
